import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

const inp = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm outline-none focus:border-amber-400 transition-colors";

function Shell({ title, sub, children }) {
  const { settings } = useAuth();
  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="https://image.tmdb.org/t/p/w1280/xJHokMbljvjADYdit5fK5VQsXEG.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0E] via-[#0A0A0E]/60 to-purple-900/30" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <img src={settings.logo_url} alt="" className="mb-6 h-20 w-20 rounded-full" />
          <p className="font-display text-6xl leading-none">{settings.brand_message}</p>
          <p className="mt-3 text-slate-300">{settings.tagline}</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-md fade-up">
          <h1 className="font-display text-5xl">{title}</h1>
          {sub && <p className="mt-1 text-sm text-slate-400">{sub}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function GoogleButton({ label }) {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const next = new URLSearchParams(window.location.search).get("next");
    if (next) sessionStorage.setItem("auth_next", next);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-auth-btn" className="flex w-full items-center justify-center gap-3 rounded-xl border border-white/15 bg-white/5 py-3 text-sm font-semibold hover:bg-white/10">
      <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.9-5.5 3.9-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.3 14.6 2.3 12 2.3 6.6 2.3 2.3 6.6 2.3 12s4.3 9.7 9.7 9.7c5.6 0 9.3-3.9 9.3-9.5 0-.6-.1-1.1-.2-1.6H12z" /></svg>
      {label}
    </button>
  );
}

const Or = () => <div className="my-6 flex items-center gap-3 text-xs text-slate-500"><span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" /></div>;

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [p] = useSearchParams();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Sign In", noindex: true });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", f); setUser(data); toast.success(`Welcome back, ${data.name}`); nav(p.get("next") || "/"); }
    catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Sign In" sub="Pick up where you left off — on any device.">
      <GoogleButton label="Continue with Google" />
      <Or />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input data-testid="login-identifier" required value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} placeholder="Email or username" autoComplete="username" className={inp} />
        <input data-testid="login-password" required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" autoComplete="current-password" className={inp} />
        {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
        <div className="flex justify-end"><Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-amber-400 hover:underline">Forgot password?</Link></div>
        <button data-testid="login-submit" disabled={busy} className="w-full rounded-xl bg-amber-400 py-3 font-bold text-black hover:bg-amber-300 disabled:opacity-60">{busy ? "Signing in…" : "Sign In"}</button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-400">New here? <Link to="/register" data-testid="to-register" className="font-semibold text-amber-400">Create Account</Link></p>
    </Shell>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", username: "", email: "", password: "", confirm: "", agree: false });
  const [errs, setErrs] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Create Account", noindex: true });
  const validate = () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Enter your full name";
    if (!/^[a-z0-9_]{3,20}$/.test(f.username.toLowerCase())) e.username = "3–20 characters: letters, numbers, underscore";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email";
    if (f.password.length < 8 || !/[A-Za-z]/.test(f.password) || !/\d/.test(f.password)) e.password = "8+ characters with a letter and a number";
    if (f.confirm !== f.password) e.confirm = "Passwords don't match";
    if (!f.agree) e.agree = "Please accept the Terms and Privacy Policy";
    setErrs(e); return !Object.keys(e).length;
  };
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { name: f.name, username: f.username.toLowerCase(), email: f.email, password: f.password, agree: f.agree });
      setUser(data); toast.success("Account created — welcome to Shivam Cine Core!"); nav("/");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const field = (k, ph, type = "text", ac) => (
    <div>
      <input data-testid={`register-${k}`} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} autoComplete={ac} className={inp} />
      {errs[k] && <p className="mt-1 text-xs text-red-400" data-testid={`register-${k}-error`}>{errs[k]}</p>}
    </div>
  );
  return (
    <Shell title="Create Account" sub="Rate, review, save and join the community. Free forever.">
      <GoogleButton label="Sign up with Google" />
      <Or />
      <form onSubmit={submit} noValidate className="space-y-3" data-testid="register-form">
        {field("name", "Full name", "text", "name")}
        {field("username", "Username (e.g. cinephile_21)", "text", "username")}
        {field("email", "Email address", "email", "email")}
        {field("password", "Password", "password", "new-password")}
        {field("confirm", "Confirm password", "password", "new-password")}
        <label className="flex items-start gap-2 pt-1 text-xs text-slate-400">
          <input data-testid="register-agree" type="checkbox" checked={f.agree} onChange={(e) => setF({ ...f, agree: e.target.checked })} className="mt-0.5 accent-amber-400" />
          <span>I agree to the <Link to="/page/terms" className="text-amber-400">Terms of Service</Link> and <Link to="/page/privacy" className="text-amber-400">Privacy Policy</Link>.</span>
        </label>
        {errs.agree && <p className="text-xs text-red-400">{errs.agree}</p>}
        {err && <p className="text-sm text-red-400" data-testid="register-error">{err}</p>}
        <button data-testid="register-submit" disabled={busy} className="w-full rounded-xl bg-amber-400 py-3 font-bold text-black hover:bg-amber-300 disabled:opacity-60">{busy ? "Creating…" : "Create Account"}</button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-400">Already a member? <Link to="/login" className="font-semibold text-amber-400">Sign In</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  useSeo({ title: "Reset password", noindex: true });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.post("/auth/forgot-password", { email }); setRes(data); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Shell title="Forgot Password" sub="We'll send you a link to set a new password.">
      {res ? (
        <div className="space-y-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm" data-testid="forgot-result">
          <p>{res.message}</p>
          {res.dev_reset_link && <p className="text-xs text-slate-400">Email delivery isn't configured yet, so here's your link: <Link to={res.dev_reset_link} data-testid="dev-reset-link" className="font-semibold text-amber-400 break-all">Reset my password</Link></p>}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your account email" className={inp} />
          {err && <p className="text-sm text-red-400">{err}</p>}
          <button data-testid="forgot-submit" className="w-full rounded-xl bg-amber-400 py-3 font-bold text-black">Send reset link</button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="text-amber-400">Back to Sign In</Link></p>
    </Shell>
  );
}

export function Reset() {
  const [p] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.a !== pw.b) return setErr("Passwords don't match");
    try { await api.post("/auth/reset-password", { token: p.get("token"), password: pw.a }); toast.success("Password updated — please sign in"); nav("/login"); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Shell title="New Password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <input data-testid="reset-password" type="password" required minLength={8} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} placeholder="New password" className={inp} />
        <input data-testid="reset-confirm" type="password" required value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} placeholder="Confirm new password" className={inp} />
        {err && <p className="text-sm text-red-400" data-testid="reset-error">{err}</p>}
        <button data-testid="reset-submit" className="w-full rounded-xl bg-amber-400 py-3 font-bold text-black">Update password</button>
      </form>
    </Shell>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid }).then(({ data }) => {
      setUser(data);
      const next = sessionStorage.getItem("auth_next") || "/";
      sessionStorage.removeItem("auth_next");
      window.history.replaceState(null, "", window.location.pathname);
      nav(next, { replace: true });
    }).catch((e) => { toast.error(errMsg(e)); setUser(false); nav("/login", { replace: true }); });
  }, [loc.hash, nav, setUser]);
  return null;
}
